import { useCallback, useMemo, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

import toast from "components/partials/toast/toast";

import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentClient } from "hooks/api/use-clients";
import {
  useGetTouchpointContentTypesQuery,
  useClassTypeOptions,
  usePopulationTypeOptions,
} from "hooks/api/dictionary";

import { getErrorMessage } from "utilities";

import { LoadingButtonState } from "ui/button";
import { OptionType } from "ui/select";
import { AddEmailFormData } from "components/modals/creative-modal/creative-modal-types";

import { EMAIL_FORM_VALIDATION_SCHEMA } from "components/modals/creative-modal/creative-modal-consts";
import { useProgramTemplatesOptions } from "components/modals/creative-modal/hooks/use-program-templates-options";
import { submitCreateEmailFromTemplate } from "components/modals/creative-modal/helpers/submit-create-email-from-template";
import { submitCreateBlankEmail } from "components/modals/creative-modal/helpers/submit-create-blank-email";
import { CampaignTouchpointSummaryDTOType } from "generated/schemas";
import { useCreateTouchpoint } from "hooks/api/use-touchpoints";

export const useAddEmail = (
  isOpen: boolean,
  onClose?: () => void,
  onConfirmClose?: () => void,
  folderId?: string,
) => {
  const navigate = useNavigate();
  const currentClient = useCurrentClient();
  const defaultCampaign = useCurrentCampaign();

  const [isFormValid, setIsFormValid] = useState(false);
  const [submitState, setSubmitState] = useState<LoadingButtonState>(LoadingButtonState.Default);

  const defaultValues: Partial<AddEmailFormData> = useMemo(
    () => ({
      emailTemplateId: "",
      name: "",
      populationType: "",
      contentType: "",
      applyClientTheme: true,
      folderId: folderId,
      insightsActivator: true,
      classes: [],
    }),
    [folderId],
  );

  const methods = useForm<AddEmailFormData>({
    mode: "onChange",
    defaultValues: defaultValues,
    resolver: EMAIL_FORM_VALIDATION_SCHEMA,
  });

  const {
    reset,
    setValue,
    watch,
    formState: { isValid, isDirty },
  } = methods;

  const selectedEmailTemplateId = watch("emailTemplateId");
  const currentContentType = watch("contentType");

  const {
    templates: emailTemplates,
    isLoading: isLoadingEmailTemplates,
    templateSelectOptions: emailTemplateOptions,
  } = useProgramTemplatesOptions({
    clientId: currentClient?.id,
    touchpointType: CampaignTouchpointSummaryDTOType.EMAIL,
    enabled: isOpen,
  });

  // Use the current campaign for content types
  const selectedCampaign = defaultCampaign;

  const campaignObjective = selectedCampaign.campaignObjective || defaultCampaign.campaignObjective;

  const { data: contentTypes = [] } = useGetTouchpointContentTypesQuery(campaignObjective);
  const { classTypeOptions } = useClassTypeOptions(campaignObjective);
  const { populationTypeOptions } = usePopulationTypeOptions();

  const contentTypeOptions: OptionType[] = useMemo(() => {
    return contentTypes.map((type) => ({
      label: type.description,
      value: type.name,
    }));
  }, [contentTypes]);

  // Reset form when modal closes to ensure clean state on next open
  useEffect(() => {
    if (!isOpen) {
      reset(defaultValues);
      setSubmitState(LoadingButtonState.Default);
    }
    // eslint-disable-next-line
  }, [isOpen, reset]);

  useEffect(() => {
    if (!isOpen) return;
    if (folderId !== undefined) {
      setValue("folderId", folderId, { shouldValidate: true });
    }
  }, [folderId, isOpen, setValue]);

  useEffect(() => {
    if (!contentTypes?.length) {
      return;
    }

    if (!isOpen) {
      return;
    }

    if (contentTypes.length === 1) {
      if (currentContentType !== contentTypes[0].name) {
        setValue("contentType", contentTypes[0].name, {
          shouldValidate: true,
        });
      }
    }
  }, [contentTypes, currentContentType, setValue, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!selectedEmailTemplateId) {
      setValue("applyClientTheme", true);
      return;
    }

    const template = emailTemplates.find(
      (template) => template.touchpointId === selectedEmailTemplateId,
    );

    if (template?.applyClientTheme != null) {
      setValue("applyClientTheme", template.applyClientTheme);
    }
  }, [selectedEmailTemplateId, emailTemplates, isOpen, setValue]);

  useEffect(() => {
    setIsFormValid(isValid);
  }, [isValid, isDirty]);

  const handleCancelClick = () => {
    if (isDirty && onConfirmClose) {
      onConfirmClose();
    } else if (onClose) {
      onClose();
    }
  };
  const touchpointMutation = useCreateTouchpoint();

  const handleAddEmailSubmit = useCallback(
    async (data: AddEmailFormData) => {
      setSubmitState(LoadingButtonState.Loading);

      if (!data.contentType || !data.populationType) {
        setSubmitState(LoadingButtonState.Default);
        return;
      }

      try {
        const newEmailPath = data.emailTemplateId
          ? await submitCreateEmailFromTemplate({
              data,
              emailTemplates,
              clientId: currentClient.id,
              campaignId: defaultCampaign.id,
            })
          : await submitCreateBlankEmail({
              data,
              clientId: currentClient.id,
              campaignId: defaultCampaign.id,
              createTouchpoint: touchpointMutation.mutateAsync,
            });
        if (newEmailPath) {
          toast.success({ data: { title: "Created email" } });
          navigate(newEmailPath);
        } else {
          setSubmitState(LoadingButtonState.Default);
        }
      } catch (err) {
        toast.error({
          data: {
            title: "Failed to create email",
            message: getErrorMessage(err?.response?.data),
          },
        });
        setSubmitState(LoadingButtonState.Default);
      }
    },
    [currentClient.id, defaultCampaign.id, emailTemplates, navigate, touchpointMutation],
  );

  return {
    methods,
    isFormValid,
    submitState,
    handleAddEmailSubmit,
    handleCancelClick,
    contentTypes,
    contentTypeOptions,
    emailTemplates,
    emailTemplateOptions,
    isLoadingEmailTemplates,
    classTypeOptions,
    populationTypeOptions,
  };
};
